import { Checkbox, Dropdown, Field, RadioGroup } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection } from 'Components/Layout';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { getLabelForPortionState } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { StateOfMaterial } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import { ISieveSize, SieveSizeId } from 'Data/ViewModels/Equipment/ISieveSize';
import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import React from 'react';
import { useSelector } from 'react-redux';
import { AggregateType, AsNominalParticleSize } from '../../types';

let AggsoilSpecimenPreparationSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const isMaterialWashed = useSelector(
    pipe(createWorksheetSelector!('Preparation.MaterialWashed'), valueOrDefault(false))
  );

  const anyMassEntered = useSelector(
    pipe(
      createWorksheetSelector!(
        'WholePortion.Washing.AssociatedWashTest.MeasuredTest.Masses.MeasuredWashedConstantDryMass.DryingTray'
      ),
      (masses) =>
        !!masses?.ContainerMass ||
        !!masses?.WetSoilAndContainerMass ||
        masses?.DryMassReadings?.some((r: any) => r.DrySoilMass)
    )
  );

  const supportedNominalParticleSize: SieveSizeId[] = ['75_0', '40_0', '28_0', '20_0', '14_0', '10_0', '7_0', '5_0'];

  const availableSieveSizes = useSelector(
    pipe(createWorksheetSelector!('data.Equipment.AllSieveSizes', false), (sieveSize) =>
      sieveSize.filter((s: ISieveSize) => supportedNominalParticleSize.includes(s.Id))
    ),
    deepEquals
  );

  const nominalParticleSizeOptions = availableSieveSizes
    .map((s: ISieveSize) => ({ label: s.Caption, value: s.Id }))
    .concat([
      { value: AsNominalParticleSize.FineAggregate, label: 'Fine Aggregate' },
      { value: AsNominalParticleSize.Filler, label: 'Filler' },
    ]);

  return (
    <ListFieldSection>
      <Field
        input={<RadioGroup />}
        name="WholePortion.Preparation.StateBeforePreparation"
        options={[
          { label: getLabelForPortionState(StateOfMaterial.Moist), value: StateOfMaterial.Moist },
          { label: getLabelForPortionState(StateOfMaterial.OvenDry), value: StateOfMaterial.OvenDry },
        ]}
        confirmText="Changing the condition of received sample may clear data. Are you sure you want to continue?"
      />
      <Field
        input={<Dropdown />}
        name="Preparation.VisualNominalParticleSize"
        options={nominalParticleSizeOptions}
        simpleValue
      />

      <Field
        input={<RadioGroup />}
        name="Preparation.AggregateType"
        options={[
          { label: 'Graded aggregate', value: AggregateType.GradedAggregate },
          { label: 'One-sized aggregate', value: AggregateType.OneSizedAggregate },
        ]}
      />
      <Field input={<Checkbox />} name="Preparation.MaterialContainsAsphaltFiller" />

      <Field
        input={<Checkbox />}
        name="Preparation.MaterialWashed"
        confirmText="This action will clear any entered washing data. Are you sure you want to continue?"
        confirm={isMaterialWashed && anyMassEntered}
      />
    </ListFieldSection>
  );
};

AggsoilSpecimenPreparationSection = withInjectedProps(AggsoilSpecimenPreparationSection);

export { AggsoilSpecimenPreparationSection };
export default AggsoilSpecimenPreparationSection;
